import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '../../context/ToastContext'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { SkeletonTable } from '../../components/ui/Skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table'
import * as withdrawals from '../../services/withdrawalRequestsService'
import type { AdminWithdrawalRow } from '../../services/withdrawalRequestsService'
import * as platformOpsSettingsApi from '../../services/platformOpsSettingsApi'
import { FeatureToggle } from '../../components/ui/FeatureToggle'
import { WithdrawalActionModal } from './WithdrawalActionModal'
import { storeAuditPath, type AuditNavState } from './storeAuditRoutes'

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Disbursed' },
  { value: 'rejected', label: 'Rejected' },
] as const

const PAGE_SIZES = [10, 20, 50] as const

export default function WithdrawalsPage() {
  const navigate = useNavigate()
  const { pushToast } = useToast()
  const [searchParams] = useSearchParams()
  const initialStatus = searchParams.get('status') ?? ''
  const [items, setItems] = useState<AdminWithdrawalRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(20)
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const [actionKind, setActionKind] = useState<'approve' | 'disburse' | 'reject' | null>(null)
  const [actionRow, setActionRow] = useState<AdminWithdrawalRow | null>(null)
  const [actionSubmitting, setActionSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [requireApproval, setRequireApproval] = useState(true)
  const [opsLoading, setOpsLoading] = useState(true)
  const [opsBusy, setOpsBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setOpsLoading(true)
      try {
        const settings = await platformOpsSettingsApi.getPlatformOpsSettings()
        if (!cancelled) setRequireApproval(settings.withdrawalsRequireApproval)
      } catch {
        /* keep default true */
      } finally {
        if (!cancelled) setOpsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const refresh = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const data = await withdrawals.listWithdrawalRequests({
        page,
        limit: pageSize,
        status: statusFilter
          ? (statusFilter as 'pending' | 'approved' | 'paid' | 'rejected')
          : undefined,
      })
      setItems(data.items || [])
      setTotal(data.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, statusFilter])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function confirmWithdrawalAction(note: string) {
    if (!actionRow || !actionKind) return
    setActionSubmitting(true)
    setBusyId(actionRow.id)
    setActionError(null)
    setError(null)
    try {
      if (actionKind === 'approve') {
        await withdrawals.approveWithdrawalRequest(actionRow.id, note)
        pushToast({ message: 'Withdrawal approved', variant: 'success' })
      } else if (actionKind === 'reject') {
        await withdrawals.rejectWithdrawalRequest(actionRow.id, note)
        pushToast({ message: 'Withdrawal rejected', variant: 'success' })
      } else {
        await withdrawals.markWithdrawalDisbursed(actionRow.id, note)
        pushToast({ message: 'Withdrawal marked disbursed', variant: 'success' })
      }
      setActionKind(null)
      setActionRow(null)
      setActionError(null)
      await refresh()
    } catch (e) {
      const fallback =
        actionKind === 'approve'
          ? 'Approve failed'
          : actionKind === 'reject'
            ? 'Reject failed'
            : 'Mark disbursed failed'
      const msg = e instanceof Error ? e.message : fallback
      setActionError(msg)
    } finally {
      setActionSubmitting(false)
      setBusyId(null)
    }
  }

  function openApproveModal(row: AdminWithdrawalRow) {
    setActionError(null)
    setActionKind('approve')
    setActionRow(row)
  }

  function openRejectModal(row: AdminWithdrawalRow) {
    setActionError(null)
    setActionKind('reject')
    setActionRow(row)
  }

  function openDisburseModal(row: AdminWithdrawalRow) {
    setActionError(null)
    setActionKind('disburse')
    setActionRow(row)
  }

  function closeActionModal() {
    if (actionSubmitting) return
    setActionKind(null)
    setActionRow(null)
    setActionError(null)
  }

  function openAudit(row: AdminWithdrawalRow) {
    const state: AuditNavState = {
      storeName: row.storeName ?? `Store #${row.storeId}`,
      organisationName: row.organisationName ?? `Organisation #${row.organisationId}`,
      fromWithdrawalId: row.id,
    }
    navigate(storeAuditPath(row.storeId), { state })
  }

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const initialLoading = loading && items.length === 0

  function statusBadge(status: string) {
    const s = status === 'paid' ? 'disbursed' : status
    const base =
      'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset tabular-nums'
    if (status === 'pending') {
      return <span className={`${base} bg-amber-500/15 text-amber-700 ring-amber-500/25 dark:text-amber-400`}>Pending</span>
    }
    if (status === 'approved') {
      return <span className={`${base} bg-sky-500/15 text-sky-700 ring-sky-500/25 dark:text-sky-300`}>Approved</span>
    }
    if (status === 'paid') {
      return <span className={`${base} bg-emerald-500/15 text-emerald-800 ring-emerald-500/25 dark:text-emerald-400`}>Disbursed</span>
    }
    if (status === 'rejected') {
      return <span className={`${base} bg-rose-500/15 text-rose-800 ring-rose-500/25 dark:text-rose-400`}>Rejected</span>
    }
    return <span className={`${base} bg-muted text-muted-foreground ring-border`}>{s}</span>
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 pb-10">
      <Card padding={false} className="overflow-hidden">
        <div className="border-b border-border bg-gradient-to-br from-muted/40 via-muted/20 to-transparent px-6 py-6 sm:px-8 sm:py-8 [&>div]:mb-0">
          <CardHeader
            title="Merchant withdrawals"
            description="Review store activity before approving. Open Audit to see transactions and orders, then Approve. After the transfer (or bank wire), mark Disbursed."
          />
        </div>

        <div className="space-y-6 px-6 py-6 sm:px-8 sm:pb-8">
          <FeatureToggle
            label="Require approval before payouts"
            description="When on, auto-withdraw creates a pending request instead of transferring immediately. Manual withdrawals always use Approve → Mark disbursed."
            checked={requireApproval}
            disabled={opsLoading || opsBusy}
            onChange={(checked) => {
              void (async () => {
                setOpsBusy(true)
                try {
                  const next = await platformOpsSettingsApi.patchPlatformOpsSettings({
                    withdrawalsRequireApproval: checked,
                  })
                  setRequireApproval(next.withdrawalsRequireApproval)
                  pushToast({ message: 'Payout approval setting saved', variant: 'success' })
                } catch (e) {
                  pushToast({
                    message: e instanceof Error ? e.message : 'Failed to update setting',
                    variant: 'error',
                  })
                } finally {
                  setOpsBusy(false)
                }
              })()
            }}
          />

          <div className="flex flex-col gap-4 rounded-2xl border border-border/80 bg-muted/15 p-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</span>
                <select
                  className="min-h-10 min-w-[10.5rem] rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm"
                  value={statusFilter}
                  onChange={(e) => {
                    setPage(1)
                    setStatusFilter(e.target.value)
                  }}
                >
                  {STATUS_FILTERS.map((f) => (
                    <option key={f.value || 'all'} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Rows per page</span>
                <select
                  className="min-h-10 min-w-[5.5rem] rounded-xl border border-border bg-card px-3 py-2 text-sm text-foreground shadow-sm"
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1)
                    setPageSize(Number(e.target.value))
                  }}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:justify-end">
              {!loading && total > 0 ? (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{total}</span> request{total === 1 ? '' : 's'} total
                </p>
              ) : null}
              <Button type="button" variant="secondary" size="sm" onClick={() => void refresh()} disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
            </div>
          </div>

          {error ? (
            <p
              className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
              role="alert"
            >
              {error}
            </p>
          ) : null}

          <div className="min-h-[280px]">
            {initialLoading ? (
              <SkeletonTable rows={6} columns={8} />
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-border/90 bg-card/30">
                <Table variant="plain" className="!rounded-none !border-0 !shadow-none">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-12 whitespace-nowrap">S/N</TableHead>
                      <TableHead className="w-14 whitespace-nowrap">ID</TableHead>
                      <TableHead className="min-w-[140px]">Organisation</TableHead>
                      <TableHead className="min-w-[140px]">Store</TableHead>
                      <TableHead className="whitespace-nowrap">Amount</TableHead>
                      <TableHead className="whitespace-nowrap">Status</TableHead>
                      <TableHead className="min-w-[160px]">Internal note</TableHead>
                      <TableHead className="min-w-[200px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <td colSpan={8} className="px-4 py-16 text-center">
                          <p className="text-sm font-medium text-foreground">No withdrawal requests</p>
                          <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
                            When merchants submit a payout from store admin, it will appear here for review.
                          </p>
                        </td>
                      </TableRow>
                    ) : (
                      items.map((row, index) => (
                        <TableRow key={row.id} className="align-top">
                          <TableCell className="tabular-nums text-muted-foreground">
                            {(page - 1) * pageSize + index + 1}
                          </TableCell>
                          <TableCell className="tabular-nums text-muted-foreground">{row.id}</TableCell>
                          <TableCell>
                            <div className="font-medium leading-snug">{row.organisationName ?? '—'}</div>
                            <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">org {row.organisationId}</div>
                          </TableCell>
                          <TableCell>
                            <div className="leading-snug">{row.storeName ?? '—'}</div>
                            <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">store {row.storeId}</div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap tabular-nums">
                            <span className="font-medium text-foreground">
                              {row.currency} {row.amount}
                            </span>
                            {row.stripeConnectReady && row.status === 'approved' && row.transferReady === false ? (
                              <p className="mt-1 max-w-[12rem] whitespace-normal text-[11px] font-normal leading-snug text-amber-700 dark:text-amber-400">
                                Transfer not ready
                                {row.nextReadyAt
                                  ? ` · clears ~${new Date(row.nextReadyAt).toLocaleString(undefined, {
                                      dateStyle: 'short',
                                      timeStyle: 'short',
                                    })}`
                                  : ''}
                              </p>
                            ) : null}
                          </TableCell>
                          <TableCell>{statusBadge(row.status)}</TableCell>
                          <TableCell className="max-w-[14rem]">
                            {row.adminNote?.trim() ? (
                              <p
                                className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground line-clamp-4"
                                title={row.adminNote}
                              >
                                {row.adminNote}
                              </p>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-end">
                              <Button type="button" size="sm" variant="secondary" onClick={() => openAudit(row)}>
                                Audit
                              </Button>
                              {row.status === 'pending' ? (
                                <>
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => openApproveModal(row)}
                                    disabled={busyId === row.id}
                                  >
                                    {busyId === row.id ? '…' : 'Approve'}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => openRejectModal(row)}
                                    disabled={busyId === row.id}
                                  >
                                    Reject
                                  </Button>
                                </>
                              ) : null}
                              {row.status === 'approved' ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => openDisburseModal(row)}
                                  disabled={busyId === row.id}
                                >
                                  {busyId === row.id ? '…' : 'Mark disbursed'}
                                </Button>
                              ) : null}
                              {row.status === 'paid' || row.status === 'rejected' ? (
                                <span className="flex items-center justify-center py-1.5 text-xs text-muted-foreground sm:justify-end">
                                  {row.status === 'paid' ? 'Complete' : 'Closed'}
                                </span>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {!loading && total > 0 ? (
            <div className="flex flex-col gap-4 rounded-2xl border border-border bg-muted/20 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                <span className="tabular-nums font-medium text-foreground">
                  {from}–{to}
                </span>{' '}
                of <span className="tabular-nums font-medium text-foreground">{total}</span>
                <span className="hidden sm:inline"> · </span>
                <span className="block sm:inline">
                  Page <span className="tabular-nums font-medium text-foreground">{page}</span> of {totalPages}
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <WithdrawalActionModal
        open={actionKind != null && actionRow != null}
        kind={actionKind}
        row={actionRow}
        submitting={actionSubmitting}
        error={actionError}
        onClose={closeActionModal}
        onConfirm={(note) => void confirmWithdrawalAction(note)}
      />
    </div>
  )
}
